import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ListItem, TextField, ClickAwayListener } from '@material-ui/core';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoneIcon from '@mui/icons-material/Done';
import { makeStyles, Theme } from '@material-ui/core/styles';
import atm from 'src/App/assets/atm.svg';
import buildingImg from 'src/App/assets/building_subscription_modal_image.jpg';
import logoMobile from 'src/App/assets/logoMobile.jpg';
import FileDownloadSharpIcon from '@mui/icons-material/FileDownloadSharp';
import {
  List,
  Card,
  CardContent,
  CircularProgress,
  InputAdornment,
  Grid,
  Box,
  Typography,
  Button,
  FormControl,
  Backdrop,
  Tooltip,
  Divider,
  FormControlLabel,
  RadioGroup,
  Radio,
  ListItemIcon,
  ListItemText,
  Avatar,
  Link,
} from '@mui/material';

import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import {
  COLOR_BLACK,
  COLOR_RED,
  COLOR_GREY,
  COLOR_PRIMARY,
  COLOR_WHITE,
  PAYMENT_MODAL_GREEN,
  CountryCodeWithCurrency,
  CurrencyWithSubscriptionPrice,
  SUBSCRIPTION_MODAL_DATA_GRID_WIDTH,
  SUBSCRIPTION_MODAL_DATA_GRID_WIDTH_FOR_MOBILE,
  exchangeRates,
  FEATURE_LIST,
  TESTIMONIALS,
  COLOR_ACCENT_2,
  COLOR_ACCENT_1,
  PRICING_TABLE_TOOLTIPS,
} from 'src/utils/const';
import { Payment } from 'src/App/Dashboards/Market/Components/Payment';
import { SubscriptionModalPropTypes } from 'src/interfaces/iproptypes';
import {
  calculateCouponChange,
  checkPromocodeValidity,
  closeKeyboard,
  formatCurrencyBasedOnCountryCode,
  getCountryDataFromCountryCode,
  updateUserOpenedSubscriptionModalInDynamoDB,
} from 'src/utils/functions';
import { callApi } from 'src/core/callApi';
import ErrorIcon from '@mui/icons-material/Error';

import {
  COLOR_BACKGROUND_1,
  COLOR_ERROR_BACKGROUNG,
  COLOR_GREY_400,
  COLOR_GREY_600,
  COLOR_PRIMARY_2,
  COLOR_PRIMARY_2_100,
  COLOR_TEXT_1,
  COLOR_TEXT_4,
  SIDEBAR_COLOR,
  SUBSCRIPTION_MODAL_DARK_THEME_COLOR,
  SUBSCRIPTION_MODAL_DISCOUNT_BACKGROUND_COLOR,
  SUBSCRIPTION_MODAL_DISCOUNT_TEXT_COLOR,
  SUBSCRIPTION_MODAL_LIGHT_BACKGROUND_COLOR,
} from 'src/utils/colors';
import DemoBookingForm from 'src/App/Dashboards/Market/Components/Forms/DemoBookingForm';
import SubscriptionModalDataGrid from './Components/subscriptionModalDataGrid';
import { setLeadFormSource } from 'src/store/Dashboard/actions';
import SubscriptionTierCard from './Components/SubscriptionTierCard';
import { logAmplitudeEvent } from 'src/utils/analytics';
import { TestimonialCard, LoadingCard } from 'src/App/Dashboards/Market/Components/Cards';
import SenjaWidget from 'src/App/Dashboards/Market/Components/SenjaWidget';

import InsightsIcon from '@mui/icons-material/Insights';
import CalculateRoundedIcon from '@mui/icons-material/CalculateRounded';
import DrawRoundedIcon from '@mui/icons-material/DrawRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import TravelExploreRoundedIcon from '@mui/icons-material/TravelExploreRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlaylistAddRoundedIcon from '@mui/icons-material/PlaylistAddRounded';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import { StripeAccount } from 'src/interfaces/idashboard';
import TooltipNew from 'src/App/Dashboards/Market/Components/Tooltips/TooltipNew';

const useStyles = makeStyles((theme: Theme) => ({
  paperModal: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '100vw',
    overflowY: 'scroll',
    overflowX: 'hidden',
    boxShadow: theme.shadows[5],
    backgroundColor: '#fff',
    [theme.breakpoints.down(960)]: {
      height: '60%',
      overflowY: 'scroll',
      padding: theme.spacing(4, 4, 4),
    },
  },
  paymentBorder: {
    borderRadius: 10,
    padding: theme.spacing(2, 3, 2),
    boxShadow: theme.shadows[5],
    [theme.breakpoints.down(960)]: {
      marginBottom: 40,
    },
  },
  couponField: {
    marginTop: 10,
    marginBottom: theme.spacing(1),
    width: '100%',
  },
  switchContainer: {
    backgroundColor: COLOR_PRIMARY,
    borderRadius: 50,
    width: 115,
    height: 25,
  },
  switch: {
    borderRadius: 50,
    height: 20,
    width: 55,
    padding: 5,
  },
  switchIcon: {
    color: COLOR_WHITE,
  },
  switchIconActive: {
    color: COLOR_BLACK,
  },
  switchActive: {
    backgroundColor: COLOR_WHITE,
    '&:hover': {
      backgroundColor: COLOR_WHITE,
    },
  },
}));

const productNameToTitleMap = district => {
  return {
    starter: district && !district.includes('Brighton and Hove') ? district : '1 Market',
    standard:
      district && !district.includes('Brighton and Hove') ? (
        <span style={{ display: 'inline-block', verticalAlign: 'middle' }}>
          <span
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '50px',
              display: 'inline-block',
              verticalAlign: 'middle',
            }}
          >
            {district}
          </span>
          &nbsp;+ 4 Markets
        </span>
      ) : (
        '5 Markets'
      ),
    enterprise:
      district && !district.includes('Brighton and Hove') ? (
        <span style={{ display: 'inline-block', verticalAlign: 'middle' }}>
          <span
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '50px',
              display: 'inline-block',
              verticalAlign: 'middle',
            }}
          >
            {district}
          </span>
          &nbsp;+ 29 Markets
        </span>
      ) : (
        '30 Markets'
      ),
    monthly: 'Monthly',
    yearly: 'Yearly',
    quarterly: 'Quarterly',
  };
};

const productStateVarToNameMap = {
  pro: 'Insight Pro',
  expert: 'Analytics Expert',
  scale: 'Market Intelligence Scale',
};

const PlanSwitchGroup = ({
  values,
  type,
  setType,
  float,
  district,
  duration,
  discountPercentForYearly,
  discountPercentForQuarterly,
  monthlyButtonDisabled,
  product,
}: {
  values: string[];
  type: string;
  setType: React.Dispatch<React.SetStateAction<string>>;
  float: string;
  district: string;
  duration: string;
  discountPercentForYearly: number;
  discountPercentForQuarterly: number;
  monthlyButtonDisabled: boolean;
  product: string;
}) => {
  const buttonStyle: any = {
    background: PAYMENT_MODAL_GREEN,
    // borderRadius: 200,
    color: '#000',
    border: 'none !important',
    textTransformation: 'capitalize',
    transition: 'background 0.3s ease',
    '&:hover': {
      background: SUBSCRIPTION_MODAL_LIGHT_BACKGROUND_COLOR,
    },
  };
  return (
    <Box
      // variant="contained"
      // size="small"
      sx={{
        borderRadius: '10px',
        background: COLOR_PRIMARY_2,
        float,
        padding: '7px 3px',
      }}
    >
      {values.map((data, i) =>
        monthlyButtonDisabled && data == 'monthly' ? (
          <Tooltip title="Global plan is only available with yearly plan.">
            <Button
              key={i}
              // size="medium"
              sx={{
                ...buttonStyle,
                background: SUBSCRIPTION_MODAL_LIGHT_BACKGROUND_COLOR,
                color: COLOR_GREY_400,
                '&:hover': {
                  background: SUBSCRIPTION_MODAL_LIGHT_BACKGROUND_COLOR,
                  color: COLOR_GREY_400,
                },
                borderRadius: '10px',
                padding: '2px 8px !important',
              }}
            >
              <Typography sx={{ fontSize: '16px' }}>
                {productNameToTitleMap(district)[data.toLowerCase()]
                  ? productNameToTitleMap(district)[data.toLowerCase()]
                  : data}
              </Typography>
            </Button>
          </Tooltip>
        ) : (
          <Button
            key={i}
            // size="sma10COLOR_PRIMARY_2_100
            onClick={() => {
              setType(data);
            }}
            sx={{
              ...buttonStyle,
              background: type === data ? 'white' : COLOR_PRIMARY_2,
              color: type === data ? 'black' : COLOR_PRIMARY_2_100,
              '&:hover': {
                background: type === data ? 'white' : COLOR_PRIMARY_2,
                color: type === data ? 'black' : 'white',
              },
              borderRadius: '10px',
              padding: '7px 10px !important',
              margin: '0 5px !important',
            }}
          >
            <Typography sx={{ fontSize: '16px', fontWeight: 500 }}>
              {productNameToTitleMap(district)[data.toLowerCase()]
                ? productNameToTitleMap(district)[data.toLowerCase()]
                : data}
            </Typography>
            {data == 'yearly' || data == 'quarterly' ? (
              <Typography
                sx={{
                  fontSize: '12px',
                  backgroundColor: SUBSCRIPTION_MODAL_DISCOUNT_BACKGROUND_COLOR,
                  color: SUBSCRIPTION_MODAL_DISCOUNT_TEXT_COLOR,
                  marginLeft: '5px',
                  borderRadius: '20px',
                  padding: '2px 8px',
                  fontWeight: 'bold',
                }}
              >
                {` ${
                  data == 'yearly' ? discountPercentForYearly : discountPercentForQuarterly
                }% Off`}
              </Typography>
            ) : (
              <></>
            )}
          </Button>
        )
      )}
    </Box>
  );
};

const FeatureList = ({ plan, origin, showTrial }) => {
  const [features, setFeatures] = useState<string[]>([]);
  const [customFeatureKeys, setCustomFeatureKeys] = useState<string[]>([]);

  useEffect(() => {
    if (showTrial) {
      // Show only trial features
      console.log('test....', FEATURE_LIST['estimatorPro'].trial);
      setFeatures(FEATURE_LIST['estimatorPro'].trial || []);
      setCustomFeatureKeys([]);
    } else if (plan === 'expert') {
      const customFeaturesByOrigin = FEATURE_LIST[plan][origin] || [];
      const allFeatures = new Set([...customFeaturesByOrigin, ...FEATURE_LIST[plan].default]);
      setFeatures(Array.from(allFeatures));
      setCustomFeatureKeys(customFeaturesByOrigin);
    } else {
      setFeatures(FEATURE_LIST[plan].default || []);
    }
  }, [plan, origin, showTrial]);

  return (
    <List disablePadding={true} dense={true}>
      {features.map((feature, index) => (
        <ListItem key={index} disableGutters={true} dense={true}>
          <ListItemIcon sx={{ minWidth: '30px', marginRight: '0px' }}>
            <DoneIcon sx={{ color: 'green' }} />
          </ListItemIcon>
          {feature.includes('Money') ? (
            <ListItemText primary={<Typography sx={{ fontWeight: 500 }}>{feature}</Typography>} />
          ) : feature.includes('Download') ? (
            <ListItemText
              primary={
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                  {feature.replace('Download', '')}
                  <FileDownloadSharpIcon />
                </Typography>
              }
            />
          ) : (
            <ListItemText
              primary={customFeatureKeys.includes(feature) ? `${feature} ⭐` : feature}
            />
          )}
        </ListItem>
      ))}
    </List>
  );
};

const SubscriptionTiers = [
  // {
  //   plan: 'starter',
  //   title: 'Insight Starter',
  //   description: 'Ideal for individuals estimating Airbnb revenue or exploring a new market.',
  //   uniqueFeatures: [
  //     '1 year historical data',
  //     'Market Overview - worldwide access',
  //     '1 Airbnb revenue Report',
  //   ],
  //   uniqueFeaturesTooltip: ['historical_data', 'market_overview', 'calculator_estimator'],
  //   prevTier: null,
  // },
  // {
  //   plan: 'pro',
  //   title: 'Insight Pro',
  //   description:
  //     'Perfect for investors seeking deeper market insights and detailed property metrics.',
  //   uniqueFeatures: [
  //     '2 years historical data',
  //     'Market Details - Worldwide access',
  //     'Guest Insights ⭐',
  //     'Top 10 Host Inisghts',
  //     'Individual Listing Overview',
  //     'Listing filters',
  //     'Money back guarantee',
  //   ],
  //   prevTier: 'Starter',
  // },
  {
    plan: 'expert',
    title: 'Analytics Expert',
    description:
      'Designed for investors aiming to maximize ROI by understanding micro-neighborhood-level trends and demand.',
    uniqueFeatures: [
      '3 years historical data',
      // added from pro
      'Market Details - Worldwide access',
      'Market Explorer - 5 Searches',
      'Guest Demographics ⭐',
      'Unlimited Host Insights',
      'Listing Overview',
      'Listing filters',
      //  added from pro
      'Interactive Map Insights ⭐',
      'Amenities and Performance Filters ⭐',
      'Draw Custom Markets ⭐',
      'Unlimited Airbnb Revenue Reports',
      // 'Money back guarantee',
      'Market Compare',
      'Report Compare',
      'Top Airbnb Management (1 Markets /month) Download',
    ],
    uniqueFeaturesTooltip: [
      'historical_data',
      'market_details',
      'market_explorer',
      'guest_demographics',
      'host_performance_overview',
      'listing_overview',
      'listing_filters',
      'interactive_map_insights',
      'amenities_performance_filters',
      'draw_custom_markets',
      'calculator_estimator',
      'market_compare',
      'report_compare',
      'property_manager_view',
    ],
    prevTier: 'Starter',
  },
  {
    plan: 'scale',
    title: 'Market Intelligence Scale',
    description: 'A tailored plan for property investors and growing businesses seeking comprehensive property-level data, advanced analytics, and export capabilities.',
    uniqueFeatures: [
      'Historical Data through 2021',
      'Listing Performance Breakdown',
      'Unlimited Host Inisghts',
      'Market Explorer - 20 Searches',
      'Market Overview Table (20 Markets) Download',
      'Market Details Table (20 Markets) Download',
      'Top Airbnb Management (20 Markets /month) Download',
      'CSV Data (Market KPIs) Download',
      'CSV Data (Market Compare) Download',
      'CSV Data (Report Compare) Download',
      'CSV Data (Individual Listing) Download',
    ],
    uniqueFeaturesTooltip: [
      'historical_data',
      'listing_performance_breakdown',
      'host_performance_overview',
      'market_explorer',
      'market_table_view_discovery',
      'market_insights_export',
      'property_manager_view',
      'csv_data_downloads',
      'csv_data_downloads_market_compare',
      'csv_data_downloads_report_compare',
      'csv_data_downloads_individual_listing',
    ],
    prevTier: 'Expert',
  },
];

const SubscriptionTiersV2 = [
  {
    plan: 'expert',
    title: 'Analytics Expert',
    description:
      'Designed for investors aiming to maximize ROI by understanding micro-neighborhood-level trends and demand.',
    uniqueFeaturesMain: [
      {
        title: 'Unlimited Market Views (Overview)',
        tooltip: PRICING_TABLE_TOOLTIPS.market_details.content,
        icon: InsightsIcon,
      },
      {
        title: 'Unlimited Calculator',
        tooltip: PRICING_TABLE_TOOLTIPS.unlimited_calculator.content,
        icon: CalculateRoundedIcon,
      },
      {
        title: 'Unlimited Custom Markets & Submarkets',
        tooltip: PRICING_TABLE_TOOLTIPS.draw_custom_markets.content,
        icon: DrawRoundedIcon,
      },
      {
        title: 'Basic Listing Insight',
        tooltip: PRICING_TABLE_TOOLTIPS.basic_listing_insight.content,
        icon: HomeRoundedIcon,
      },
    ],
    uniqueFeaturesSub: [
      {
        title: '3 Years of Historical Data',
        tooltip: PRICING_TABLE_TOOLTIPS.historical_data.content,
      },
      {
        title: '5 Searches/mo: Market Explorer',
        tooltip: PRICING_TABLE_TOOLTIPS.market_explorar.content,
        icon: TravelExploreRoundedIcon,
      },
      {
        title: '1 Market/mo: Top Airbnb Management Companies',
        tooltip: PRICING_TABLE_TOOLTIPS.property_manager_view_1.content,
      },
      {
        title: '20 Saved Markets',
        tooltip: PRICING_TABLE_TOOLTIPS.saved_markets_20.content,
      },
      {
        title: 'Listings / Performance / Amenities Filters',
        tooltip: PRICING_TABLE_TOOLTIPS.filters.content,
        icon: StarRoundedIcon,
      },
      {
        title: 'Guest Insights',
        tooltip: PRICING_TABLE_TOOLTIPS.guest_demographics.content,
        icon: StarRoundedIcon,
      },
      {
        title: 'Host Insights',
        tooltip: PRICING_TABLE_TOOLTIPS.host_performance_overview.content,
        icon: StarRoundedIcon,
      },
      {
        title: '20 Calculator & Market PDF Export/mo',
        tooltip: PRICING_TABLE_TOOLTIPS.calculator_pdf_export.content,
        icon: DownloadRoundedIcon,
      },
      {
        title: 'Interactive Map',
        tooltip: PRICING_TABLE_TOOLTIPS.interactive_map_insights.content,
        icon: StarRoundedIcon,
      },
    ],
    prevTier: '',
  },
  {
    plan: 'scale',
    title: 'Market Intelligence Scale',
    description: 'A tailored plan for property investors and growing businesses seeking comprehensive property-level data, advanced analytics, and export capabilities.',
    uniqueFeaturesMain: [
      {
        title: 'Everything in Expert',
        tooltip: null,
        icon: PlaylistAddRoundedIcon,
      },
      {
        title: 'Advanced Listing Insight',
        tooltip: PRICING_TABLE_TOOLTIPS.advanced_listing_insight.content,
        icon: CategoryOutlinedIcon,
      },
      {
        title: 'Export 1,000 Rows per Month',
        tooltip: PRICING_TABLE_TOOLTIPS.export_1000_rows.content,
        icon: DownloadRoundedIcon,
      },
    ],
    uniqueFeaturesSub: [
      {
        title: '20 Searches/mo: Market Explorer',
        tooltip: PRICING_TABLE_TOOLTIPS.market_explorar_20.content,
      },
      {
        title: '20 Markets/mo: Top Airbnb Management Companies',
        tooltip: PRICING_TABLE_TOOLTIPS.property_manager_view.content,
      },
      {
        title: '100 Saved Markets',
        tooltip: PRICING_TABLE_TOOLTIPS.saved_markets_100.content,
      },
      {
        title: 'Multiple Saved Markets List',
        tooltip: PRICING_TABLE_TOOLTIPS.multiple_saved_markets_list.content,
      },
      {
        title: '100 Calculator & Market PDF Export/mo',
        tooltip: PRICING_TABLE_TOOLTIPS.calculator_pdf_export.content,
        icon: DownloadRoundedIcon,
      },
    ],
    prevTier: 'expert',
  },
];

const SubscriptionModal = ({
  countryCode,
  city,
  district,
  getTokenThunk,
  subscriptionModalOpen,
  setSubscriptionModalOpen,
  setIsTailoredRegionOpen,
  user,
  setShowUpgradeSubscriptionModal,
  basicViewPlanName,
  view,
  setView,
  origin,
  showTrial,
  setRequiredSubscriptionPlan,
  setSubscriptionModalView,
  setSubscriptionModalOrigin,
  resetSubscriptionModal,
}: SubscriptionModalPropTypes) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const isEstimatorPage = window.location.pathname.includes('/airbnb-calculator');
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down(900));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumScreenSize = useMediaQuery(theme.breakpoints.down('lg'));
  const [showDemoBookingForm, setShowDemoBookingForm] = useState<boolean>(false);
  const [isPromoCodeLoading, setIsPromoCodeLoading] = useState<boolean>(true);
  const [product, setProduct] = useState<string>('expert');
  const [duration, setDuration] = useState<string>('yearly');
  const [promoCode, setPromoCode] = useState<string>('');
  const [isPromoCodeValid, setIsPromoCodeValid] = useState(false);
  const [validPromoCode, setValidPromoCode] = useState<string>('');
  const [discountPercentForYearly, setDiscountPercentForYearly] = useState<number>(0);
  const [discountPercentForQuarterly, setDiscountPercentForQuarterly] = useState<number>(0);
  const [discountedPrice, setDiscountedPrice] = useState<null | number>(null);
  const [promoCodeDiscountsDict, setPromoCodeDiscountDict] = useState<any>(null);
  const [countryToUnlock, setCountryToUnlock] = useState<string | null>(null);
  // const [countryTier, setCountryTier] = useState<string>('tier3');
  const [defaultCountryToUnlock, setDefaultCountryToUnlock] = useState<any>(null);
  const userCurrentCountryCode = useSelector((state: any) => state.dashboard.userCountryCode);
  const [collectPayment, setCollectPayment] = useState(false);
  const [monthlyButtonDisabled, setMonthlyButtonDisabled] = useState<boolean>(false);
  //userSignupCountryCode represent the country from where he/she signed up in our app
  const userSignupCountryCode = useSelector((state: any) => state.dashboard.user.signupCountryCode);
  const userLocalCurrency = CountryCodeWithCurrency[userSignupCountryCode] || 'USD';
  const subscriptionPriceAccordingToUserLocalCurrency =
    CurrencyWithSubscriptionPrice[userLocalCurrency];
  const [stripeAccount, setStripeAccount] = useState<StripeAccount | null>(null);

  const promocodeTextFieldRef = useRef<HTMLInputElement | null>(null);
  const searchedMarketCountryCode = useSelector(
    state => (state as any).dashboard.search.data.countryCode
  );

  const [formHeadingFor, setFormHeadingFor] = useState<string>('demo');
  const paymentModalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (validPromoCode) {
      setStripeAccount('airbtics_uk');
    }
  }, [validPromoCode]);

  useEffect(() => {
    if (user.stripeAccount) {
      setStripeAccount(user.stripeAccount);
    }
  }, [user]);

  const AirbticsAudiences = [
    'Property Management Companies',
    'Tourism Boards & DMOs',
    'Researchers',
    'Property Investors',
  ];

  const AirbticsDataAvailiableIn = ['CSV', 'Dashboard', 'API'];

  const isSubscriptionUpgradable = user.subscriptionPlan == 'expert' && product === 'scale';

  const closeBookingForm = () => {
    setShowDemoBookingForm(false);
  };

  const openDemoBookingForm = () => {
    setShowDemoBookingForm(true);
  };

  useEffect(() => {
    if (isEstimatorPage) {
      setView('basic');
    }

    closeKeyboard();
  }, []);

  useEffect(() => {
    let timerId;

    if (subscriptionModalOpen) {
      timerId = setTimeout(() => {
        updateUserOpenedSubscriptionModalInDynamoDB();
      }, 3000);
    }

    return () => {
      clearTimeout(timerId);
    };
  }, [subscriptionModalOpen]);

  useEffect(() => {
    if (!subscriptionModalOpen) {
      console.log('unmount');
      resetSubscriptionModal();
    }
  }, [subscriptionModalOpen]);

  useEffect(() => {
    logAmplitudeEvent('payment_page_viewed', {
      plan_name: product,
      plan_duration: duration,
      is_upgrade: false,
      view: view,
      visited_from: origin || '',
    });
  }, [product, duration, view, origin]);

  useEffect(() => {
    let monthlyPrice = subscriptionPriceAccordingToUserLocalCurrency[product]['monthly'];
    let yearlyPricePerMonth = subscriptionPriceAccordingToUserLocalCurrency[product]['yearly'] / 12;
    let quarterlyPricePerMonth =
      subscriptionPriceAccordingToUserLocalCurrency[product]['quarterly'] / 3;

    let profitPercentile = Math.round(((monthlyPrice - yearlyPricePerMonth) / monthlyPrice) * 100);
    let profitPercentQuarterly = Math.round(
      ((monthlyPrice - quarterlyPricePerMonth) / monthlyPrice) * 100
    );

    setDiscountPercentForYearly(profitPercentile);
    setDiscountPercentForQuarterly(profitPercentQuarterly);
  }, [product, duration]);

  useEffect(() => {
    const getCoupons = async () => {
      let coupons = await callApi(
        'https://airbtics-resource.s3.us-east-2.amazonaws.com/coupons.json',
        // 'https://airbtics-resource.s3.us-east-2.amazonaws.com/couponsTest.json',
        {},
        false
      );

      //making all coupons capatalize
      for (const coupon in coupons) {
        if (!coupons[coupon.toUpperCase()]) {
          coupons[coupon.toUpperCase()] = coupons[coupon];
          delete coupons[coupon];
        }
      }
      setPromoCodeDiscountDict(coupons);
      setIsPromoCodeLoading(false);
    };
    getCoupons();

    if (searchedMarketCountryCode) {
      setDefaultCountryToUnlock(getCountryDataFromCountryCode(searchedMarketCountryCode));
    } else {
      setDefaultCountryToUnlock(getCountryDataFromCountryCode(userCurrentCountryCode));
    }
  }, []);

  useEffect(() => {
    calculateCouponChange(
      promoCode,
      promoCodeDiscountsDict,
      subscriptionPriceAccordingToUserLocalCurrency,
      duration,
      setDiscountedPrice,
      product
    );
  }, [product, duration, promoCode]);

  // useEffect(() => {
  //   const EMAIL_API_URL =
  //     'https://yg8sbi20zb.execute-api.us-east-2.amazonaws.com/default/sendEmailEventBased';
  //   const ERROR_MESSAGES_IF_EMAIL_IS_ALREADY_SET_TO_SEND = [
  //     'Time still left to send email!',
  //     'Email Already Sent!',
  //   ];

  //   const sendEmailIfEligible = async () => {
  //     if (user && user.email !== 'Trial') {
  //       const isEmailAlreadySent =
  //         user.isSubscriptionPricingModalEmailSent ||
  //         localStorage.getItem('isSubscriptionPricingModalEmailSent') === 'true' ||
  //         user.subscriptionPlan.toLowerCase() !== 'starter';

  //       if (isEmailAlreadySent) {
  //         console.log('Email sending condition already met!');
  //         return;
  //       }

  //       const response = await callApi(
  //         EMAIL_API_URL,
  //         {
  //           queryParams: {
  //             emailEventType: 'subscription_pricing_modal',
  //           },
  //         },
  //         true,
  //         false,
  //         '',
  //         'GET',
  //         true
  //       );

  //       console.log('API Response:', response);

  //       if (
  //         response?.message ||
  //         ERROR_MESSAGES_IF_EMAIL_IS_ALREADY_SET_TO_SEND.includes(response?.error)
  //       ) {
  //         localStorage.setItem('isSubscriptionPricingModalEmailSent', 'true');
  //       }
  //     }
  //   };

  //   try {
  //     sendEmailIfEligible();
  //   } catch (err) {
  //     console.error('Failed to send pricing modal email: ', err);
  //   }
  // }, [user]);

  const handleCouponChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const currentPromoCode: string = e.target.value.trim();
    setPromoCode(_ => currentPromoCode);
  };

  useEffect(() => {
    const validateAndSetPromoCode = async () => {
      setIsPromoCodeLoading(true);
      const isValid = (await checkPromocodeValidity(
        promoCode?.toUpperCase(),
        promoCodeDiscountsDict
      )) as boolean;
      const validCode = (await checkPromocodeValidity(
        promoCode?.toUpperCase(),
        promoCodeDiscountsDict,
        true
      )) as string;

      setValidPromoCode(validCode);
      setIsPromoCodeValid(isValid);
      setIsPromoCodeLoading(false);
    };

    validateAndSetPromoCode();
  }, [promoCode]);

  // useEffect(() => {
  //   return () => {
  //     resetSubscriptionModal();
  //   };
  // }, [setSubscriptionModalOpen]);

  useEffect(() => {
    return () => {
      console.log('unmount');
    };
  }, []);

  const PromoCodeSection = (
    <>
      <Grid container alignItems="center">
        <Grid item xs={isPromoCodeLoading ? 11 : 12}>
          <TextField
            placeholder="Promo Code (Optional)"
            type="text"
            className={classes.couponField}
            inputRef={promocodeTextFieldRef}
            onChange={handleCouponChange}
            InputProps={
              !isPromoCodeLoading && promoCode !== ''
                ? {
                    endAdornment: (
                      <InputAdornment position="end">
                        {isPromoCodeValid ? (
                          <CheckCircleIcon color="success" />
                        ) : (
                          <TooltipNew content="Promo code is invalid.">
                            <ErrorIcon color="error" sx={{ cursor: 'pointer' }} />
                          </TooltipNew>
                        )}
                      </InputAdornment>
                    ),
                  }
                : {}
            }
          />
        </Grid>
        {isPromoCodeLoading && (
          <Grid item xs={1} alignItems="center" justifyContent="center">
            <CircularProgress size={20} />
          </Grid>
        )}
      </Grid>
      <Typography variant="caption" color="textPrimary">
        {' '}
        Promo discounts will be applied upon checkout and in your emailed invoice
      </Typography>
    </>
  );

  const DetailedSubscriptionModal = () => (
    <Grid container direction="column" alignItems="center" justifyContent="center">
      <DemoBookingForm
        matches={matches}
        user={user}
        showDemoBookingForm={showDemoBookingForm}
        closeBookingForm={closeBookingForm}
        headingFor={formHeadingFor}
      />
      <div
        className={`${classes.paperModal} modal-custom`}
        style={{ height: '100vh', backgroundColor: SUBSCRIPTION_MODAL_DARK_THEME_COLOR }}
      >
        {/* Close BUTTON */}
        <div
          style={{
            position: 'absolute',
            right: '30px',
            top: isMobile || isMediumScreenSize ? '80px' : '30px',
            cursor: 'pointer',
          }}
          onClick={() => {
            setSubscriptionModalOpen(false);
            resetSubscriptionModal();
          }}
        >
          <CloseRoundedIcon sx={{ fontSize: '40px', color: '#ffff' }} />
        </div>

        <Grid
          container
          direction="column"
          sx={{
            alignItems: 'center',
          }}
        >
          <Grid
            container
            item
            sx={{
              ...{ maxWidth: '1600px' },
              ...(isMobile || isMediumScreenSize ? { paddingTop: '40px' } : {}),
            }}
          >
            <Grid
              item
              xs={12}
              sx={{
                minHeight: isMobile ? '75px' : '125px',
                padding: isMobile || isMediumScreenSize ? '20px' : '20px 12%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'flex-start',
                maxWidth: '1320px',
              }}
            >
              <Typography variant="h4" sx={{ color: '#ffff', fontWeight: 500 }}>
                Pricing
              </Typography>
              <Typography variant="h6" sx={{ color: '#ffff', fontWeight: 400 }}>
                Accurate and Actionable Data At Your Fingertips!
              </Typography>
            </Grid>
          </Grid>

          <Grid
            container
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            sx={{
              backgroundColor: isMobile ? COLOR_BACKGROUND_1 : '#FFFFFF',
              borderRadius: '20px 20px 0px 0px',
              padding: isMobile || isMediumScreenSize ? '20px' : '20px 5%',
              maxWidth: '1600px',
              position: 'relative',
            }}
          >
            {/* INFO */}
            <Grid
              container
              direction="column"
              justifyContent="flex-start"
              alignItems="flex-start"
              item
              // sx={{ paddingRight: '25px' }}
              lg={12}
              md={12}
              xs={12}
            >
              <Grid item xs={12} sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                <div>
                  <PlanSwitchGroup
                    // values={['monthly', 'quarterly', 'yearly']}
                    values={['yearly', 'monthly']}
                    type={duration}
                    setType={setDuration}
                    float={'right'}
                    district={district}
                    duration={duration}
                    discountPercentForYearly={discountPercentForYearly}
                    discountPercentForQuarterly={discountPercentForQuarterly}
                    monthlyButtonDisabled={monthlyButtonDisabled}
                    product={product}
                  />
                </div>
              </Grid>
              <Grid
                item
                xs={12}
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: matches ? '' : 'center',
                  marginTop: '10px',
                }}
              >
                <Box
                  sx={{
                    maxWidth: SUBSCRIPTION_MODAL_DATA_GRID_WIDTH_FOR_MOBILE,
                    width: '100%',
                    mt: 2,
                    mb: 2,
                  }}
                >
                  <Grid
                    container
                    item
                    direction="row"
                    justifyContent="center"
                    alignItems="flex-start"
                    spacing={2}
                  >
                    {SubscriptionTiersV2.map(SubscriptionTier => (
                      <Grid item xs={12} md={6}>
                        <SubscriptionTierCard
                          key={`${product}-${duration}-${userLocalCurrency}-${SubscriptionTier.plan}`}
                          plan={SubscriptionTier.plan}
                          title={SubscriptionTier.title}
                          description={SubscriptionTier.description}
                          features={SubscriptionTier.uniqueFeaturesMain}
                          subFeatures={SubscriptionTier.uniqueFeaturesSub}
                          prevTier={SubscriptionTier.prevTier}
                          userLocalCurrency={userLocalCurrency}
                          user={user}
                          duration={duration}
                          subscriptionPriceAccordingToUserLocalCurrency={
                            subscriptionPriceAccordingToUserLocalCurrency
                          }
                          promoCodeDiscountsDict={promoCodeDiscountsDict}
                          product={product}
                          setProduct={setProduct}
                          promoCode={validPromoCode}
                          isPromoCodeValid={isPromoCodeValid}
                          paymentModalRef={paymentModalRef}
                          openDemoBookingForm={openDemoBookingForm}
                          setFormHeadingFor={setFormHeadingFor}
                          setShowUpgradeSubscriptionModal={setShowUpgradeSubscriptionModal}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>

                <List style={{ maxWidth: '1230px', padding: '0px 10px' }}>
                  <ListItem
                    style={{
                      maxWidth: '1230px',
                      marginBottom: '10px',
                      paddingRight: 0,
                      paddingLeft: 0,
                    }}
                  >
                    <Grid
                      container
                      xs={12}
                      spacing={1}
                      sx={{
                        width: '100%',
                        flexWrap: 'nowrap',
                      }}
                      alignItems="flex-start"
                      justifyContent="center"
                    >
                      {/* <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            <DoneRoundedIcon
                              sx={{
                                background: PAYMENT_MODAL_GREEN,
                                color: '#fff',
                                fontWeight: 'bold',
                                fontSize: '20px',
                                borderRadius: '500px',
                                padding: '4px',
                                marginRight: '5px',
                                position: 'relative',
                                top: '7px',
                              }}
                            />
                            <span style={{ color: COLOR_PRIMARY_2 }}>
                              15-days money-back guarantee
                            </span>
                            , in the first 15 days of your first purchase, if you don't like the
                            service or don't find it useful anymore you can request a full refund we
                            will give it to you straight away.
                            <br />
                            <br />
                            This only applies if you are first time subscriber and you have not
                            unlocked for more than 2 markets. A market includes any area on the map,
                            such as a city, or neighborhood.
                            <br />
                            <br />
                            You can ask for a refund by contacting us at support@airbtics.com
                            <br />
                            <br />
                          </Typography> */}
                      <Typography
                        variant="body1"
                        sx={{
                          padding: '10px 20px',
                          backgroundColor: COLOR_PRIMARY_2 + '11',
                          color: COLOR_TEXT_1,
                          borderRadius: '200px',
                          width: isMobile ? '300px' : '450px',
                          wordWrap: 'break-word',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}
                      >
                        <StarRoundedIcon sx={{ color: COLOR_PRIMARY_2 }} /> Indicates exclusive
                        features available only with Airbtics.
                      </Typography>
                    </Grid>
                  </ListItem>
                </List>
              </Grid>
            </Grid>
            {/* payment */}
            {!isSubscriptionUpgradable && (
              <Grid
                container
                item
                direction="column"
                lg={12}
                md={12}
                xs={12}
                sx={{ padding: '25px' }}
              >
                <Grid
                  item
                  xs={12}
                  sx={{
                    position: 'absolute',
                    top: '-70px',
                    right: isMobile || isMediumScreenSize ? '20px' : '12%',
                  }}
                >
                  <img
                    src={atm}
                    style={{
                      height: isMobile ? '50px' : '100px',
                      marginTop: isMobile ? '10px' : 'none',
                    }}
                  />
                </Grid>
                <Grid
                  xs={12}
                  md={12}
                  lg={12}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Grid
                    container
                    direction="row"
                    justifyContent="flex-start"
                    item
                    xs={12}
                    md={12}
                    lg={5}
                    ref={paymentModalRef}
                  >
                    <Typography sx={{ fontWeight: 'bold', marginBottom: '20px' }}>
                      Purchase Summary
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={12} lg={5} sx={{ marginBottom: '10px', width: '100%' }}>
                    <Divider />
                  </Grid>
                  <Grid
                    container
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    item
                    xs={12}
                    md={12}
                    lg={5}
                    sx={{ marginBottom: '20px' }}
                  >
                    <Typography
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: matches ? 'space-between' : 'flex-start',
                        width: matches ? '100%' : 'auto',
                        gap: '10px',
                      }}
                    >
                      Plan:{' '}
                      <RadioGroup
                        row
                        value={product}
                        onChange={(event, value) => {
                          setProduct(value as 'expert' | 'scale');
                          user.subscriptionPlan !== 'starter' &&
                            setShowUpgradeSubscriptionModal(value as 'expert' | 'scale');
                        }}
                      >
                        <FormControlLabel value="expert" control={<Radio />} label="Expert" />
                        <FormControlLabel value="scale" control={<Radio />} label="Scale" />
                      </RadioGroup>
                    </Typography>
                    <FormControl
                      sx={{
                        minWidth: 120,
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: matches ? 'space-between' : 'flex-start',
                        width: matches ? '100%' : 'auto',
                        gap: '10px',
                      }}
                    >
                      {matches ? <Typography>Duration:</Typography> : <></>}
                      <RadioGroup
                        row
                        aria-labelledby="demo-row-radio-buttons-group-label"
                        name="row-radio-buttons-group"
                        value={duration}
                        onChange={(event, value) => {
                          setDuration(value);
                          user.subscriptionPlan !== 'starter' &&
                            setShowUpgradeSubscriptionModal(product as 'expert' | 'scale');
                        }}
                      >
                        <FormControlLabel value="yearly" control={<Radio />} label="Yearly" />
                        <FormControlLabel value="monthly" control={<Radio />} label="Monthly" />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                  <Grid container item xs={12} md={12} lg={5} className={classes.paymentBorder}>
                    {stripeAccount ? (
                      <Payment
                        key={`${product}-${duration}-${userLocalCurrency}-payment-detailed`}
                        countryCode={countryCode}
                        city={city}
                        district={district}
                        product={product}
                        duration={duration}
                        promoCode={validPromoCode}
                        setSubscriptionModalOpen={setSubscriptionModalOpen}
                        discountedPrice={discountedPrice}
                        countryToUnlock={countryToUnlock}
                        subscriptionPriceAccordingToUserLocalCurrency={
                          subscriptionPriceAccordingToUserLocalCurrency
                        }
                        userLocalCurrency={userLocalCurrency}
                        stripeAccount={stripeAccount}
                      />
                    ) : (
                      <LoadingCard height="100%" type="bars" />
                    )}
                  </Grid>
                  <Grid container item xs={12} md={12} lg={5} sx={{ marginTop: '20px' }}>
                    {PromoCodeSection}
                  </Grid>
                </Grid>
              </Grid>
            )}
            <Grid
              container
              item
              xs={12}
              sx={{
                height: 'auto',
                maxWidth: '100%',
                backgroundColor: SUBSCRIPTION_MODAL_DARK_THEME_COLOR,
                borderRadius: '20px',
                overflow: 'hidden',
              }}
            >
              {!matches && (
                <Grid md={2} item sx={{ position: 'relative' }}>
                  {' '}
                  <Box
                    // src={buildingImg}
                    sx={{
                      height: '100%',
                      width: '100%',
                      objectFit: 'cover',
                      background: `linear-gradient(88.43deg, rgba(11, 51, 58, 0) 1.34%, rgba(11, 51, 58, 0.2) 55.56%), url(${buildingImg}) center center`,
                      mixBlendMode: 'hard-light',
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      width: '3px',
                      height: '40%',
                      background: '#fff',
                      right: '-1px',
                      zIndex: '10',
                      top: '0px',
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      width: '3px',
                      height: '20%',
                      background: '#fff',
                      right: '-5px',
                      zIndex: '10',
                      top: '0px',
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      width: '3px',
                      height: '40%',
                      background: '#fff',
                      right: '-1px',
                      zIndex: '10',
                      top: '60%',
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      width: '3px',
                      height: '20%',
                      background: '#fff',
                      right: '3px',
                      zIndex: '10',
                      top: '80%',
                    }}
                  />
                </Grid>
              )}
              <Grid
                xs={12}
                md={6}
                item
                container
                sx={{
                  padding: isMobile ? '20px' : '10px 10px 10px 70px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="h4" sx={{ color: SIDEBAR_COLOR }}>
                  {' '}
                  Enterprise
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: SIDEBAR_COLOR, margin: '30px 0px', maxWidth: '270px' }}
                >
                  {' '}
                  Get Accurate and Granular Custom Short-Term Rental Data
                </Typography>
                <Button
                  sx={{
                    width: '200px',
                    borderRadius: '50px',
                    background: SIDEBAR_COLOR,
                    color: COLOR_PRIMARY_2,
                    '&:hover': {
                      background: SUBSCRIPTION_MODAL_LIGHT_BACKGROUND_COLOR,
                    },
                  }}
                  onClick={() => {
                    // postOnSlack(
                    //   'C072095QUU9',
                    //   `User: ${user.email} clicked on Talk to Sales Button In the Subscription Modal.`
                    // );
                    setFormHeadingFor('sales');
                    openDemoBookingForm();
                    dispatch(setLeadFormSource('app-pricing'));
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 500, color: COLOR_PRIMARY_2 }}>
                    Contact Us
                  </Typography>
                </Button>
              </Grid>
              <Grid
                item
                xs={12}
                md={4}
                sx={{
                  padding: '10px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="h6" sx={{ color: SIDEBAR_COLOR, fontWeight: 500 }}>
                  Our solutions are tailored for
                </Typography>
                {AirbticsAudiences.map(audience => (
                  <Box key={audience} sx={{ marginLeft: '20px' }}>
                    <Typography variant="body2" sx={{ color: '#A7F7F3', marginBottom: '5px' }}>
                      ✓ {audience}
                    </Typography>
                  </Box>
                ))}

                <Typography
                  variant="h6"
                  sx={{ color: SIDEBAR_COLOR, fontWeight: 500, marginTop: '20px' }}
                >
                  Our data is available via:
                </Typography>
                {AirbticsDataAvailiableIn.map(dataAvailiableMedium => (
                  <Box key={dataAvailiableMedium} sx={{ marginLeft: '20px' }}>
                    <Typography variant="body2" sx={{ color: '#A7F7F3', marginBottom: '5px' }}>
                      ✓ {dataAvailiableMedium}
                    </Typography>
                  </Box>
                ))}
              </Grid>
            </Grid>

            {/* Reviews */}
            <SenjaWidget isMobile={matches} />
          </Grid>
        </Grid>
      </div>
    </Grid>
  );

  const BasicSubscriptionModal = (
    planName: 'pro' | 'expert' | 'scale',
    collectPayment,
    setCollectPayment
  ) => {
    if (planName === 'pro') {
      planName = 'expert';
    }

    let priceDiscountedMonthly: any = 0;
    let priceDiscountedYearly: any = 0;

    if (discountedPrice) {
      priceDiscountedMonthly = formatCurrencyBasedOnCountryCode(
        parseFloat((discountedPrice / 12).toFixed(2)),
        userLocalCurrency
      );
      priceDiscountedYearly = formatCurrencyBasedOnCountryCode(discountedPrice, userLocalCurrency);
    }

    const headingText = showTrial
      ? 'Create Unlimited Revenue Reports'
      : `Subscribe to ${planName.charAt(0).toUpperCase() + planName.slice(1)} Plan`;

    const OptionalConfirmationPage = (
      <Grid
        item
        container
        direction="row"
        justifyContent="flex-start"
        alignItems="flex-start"
        xs={12}
        sx={{ width: '100%', padding: '12px 24px', height: '100%' }}
      >
        <Grid xs={12} container item justifyContent="flex-end" alignItems="flex-start">
          <CloseRoundedIcon
            sx={{ color: COLOR_GREY_400, cursor: 'pointer', fontWeight: 'bold' }}
            onClick={() => {
              setSubscriptionModalOpen(false);
              resetSubscriptionModal();
            }}
          />
        </Grid>

        <Grid container item>
          <Box
            sx={{
              width: '100%',
              padding: '20px',
              backgroundColor: '#e0f7fa',
              borderRadius: '8px',
              textAlign: 'center',
              boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
            }}
          >
            {/* Header with logo and details link */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '10px',
              }}
            >
              {/* Logo */}
              <img
                src={logoMobile}
                alt="Airbtics Logo"
                style={{ height: '30px', mixBlendMode: 'multiply' }}
              />

              {/* Details Link */}
              <Link
                underline="hover"
                sx={{
                  fontSize: '0.875rem',
                  color: 'text.secondary',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
                onClick={() => setCollectPayment(false)}
              >
                Details
              </Link>
            </Box>

            {/* Subscription description */}
            <Typography variant="body2" sx={{ color: 'text.secondary', marginBottom: '8px' }}>
              Try Insight Pro Subscription (15 days trial)
            </Typography>

            {/* Trial period information */}
            <Typography variant="h4" sx={{ fontWeight: 'bold', marginBottom: '4px' }}>
              15 days free
            </Typography>

            {/* Pricing information */}
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              Then {exchangeRates[userLocalCurrency].symbol}
              {discountedPrice
                ? formatCurrencyBasedOnCountryCode(discountedPrice, userLocalCurrency)
                : formatCurrencyBasedOnCountryCode(
                    subscriptionPriceAccordingToUserLocalCurrency[product][duration],
                    userLocalCurrency
                  )}{' '}
              per year
            </Typography>
          </Box>
        </Grid>

        <Grid container item className={classes.paymentBorder}>
          {stripeAccount ? (
            <Payment
              key={`${product}-${duration}-${userLocalCurrency}-payment-trial`}
              countryCode={countryCode}
              city={city}
              district={district}
              product="expert"
              duration="yearly"
              promoCode={promoCode}
              setSubscriptionModalOpen={setSubscriptionModalOpen}
              discountedPrice={discountedPrice}
              countryToUnlock={countryToUnlock}
              subscriptionPriceAccordingToUserLocalCurrency={
                subscriptionPriceAccordingToUserLocalCurrency
              }
              userLocalCurrency={userLocalCurrency}
              isTrial={true}
              stripeAccount={stripeAccount}
            />
          ) : (
            <LoadingCard height="100%" type="bars" />
          )}
        </Grid>
      </Grid>
    );

    const DefaultPage = (
      <Grid container sx={{ padding: '15px' }}>
        <Grid xs={12} container direction="row" justifyContent="space-between" alignItems="center">
          <Typography
            variant="h5"
            sx={{ color: COLOR_PRIMARY_2, fontWeight: 500, flex: 1, maxWidth: '80%' }}
          >
            {headingText}
          </Typography>
          <CloseRoundedIcon
            sx={{ color: COLOR_GREY_400, cursor: 'pointer', fontWeight: 'bold' }}
            onClick={() => {
              setSubscriptionModalOpen(false);
              resetSubscriptionModal();
            }}
          />
        </Grid>
        <Grid item xs={12} sx={{ margin: '10px 0', width: '100%' }}>
          <Divider />
        </Grid>
        <Grid item xs={12}>
          <FeatureList
            plan={isEstimatorPage ? 'estimatorPro' : basicViewPlanName}
            origin={origin}
            showTrial={showTrial}
          />
        </Grid>
        <Grid
          container
          item
          xs={12}
          justifyContent="flex-end"
          alignItems="center"
          sx={{ cursor: 'pointer' }}
        >
          <Typography
            variant="caption"
            sx={{ fontWeight: 'bold' }}
            onClick={() => setView('detailed')}
          >
            Compare all subscriptions
          </Typography>{' '}
          <OpenInNewIcon sx={{ fontSize: 12, ml: 1 }} onClick={() => setView('detailed')} />
        </Grid>

        {!showTrial && (
          <>
            <Grid container item direction="row" justifyContent="flex-start" xs={12}>
              <Typography sx={{ fontWeight: 'bold', marginTop: '20px' }}>
                Purchase Summary
              </Typography>
            </Grid>
            <Grid
              item
              container
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              xs={12}
            >
              <Typography variant="body2">Plan</Typography>
              <FormControl>
                <RadioGroup
                  row
                  aria-labelledby="demo-row-radio-buttons-group-label"
                  name="row-radio-buttons-group"
                  value={duration}
                  onChange={(event, value) => setDuration(value)}
                >
                  <FormControlLabel
                    value="yearly"
                    control={<Radio />}
                    label={
                      <Typography sx={{ textAlign: 'center', display: 'flex' }}>
                        Yearly{' '}
                        <Typography
                          sx={{
                            fontSize: '12px',
                            backgroundColor: SUBSCRIPTION_MODAL_DISCOUNT_BACKGROUND_COLOR,
                            color: SUBSCRIPTION_MODAL_DISCOUNT_TEXT_COLOR,
                            marginLeft: '5px',
                            borderRadius: '12px',
                            padding: '2px 8px',
                            fontWeight: 'bold',
                          }}
                        >
                          {discountPercentForYearly}% Off
                        </Typography>
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    sx={{ mr: 0 }}
                    value="monthly"
                    control={<Radio />}
                    label="Monthly"
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid
              item
              container
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              xs={12}
              sx={{ mb: 2, mt: 2 }}
              key={`${product}-${duration}-${userLocalCurrency}-price-basic-modal`}
            >
              <Typography variant="body2">Price</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
                <Typography
                  variant="body2"
                  style={{
                    fontWeight: 'bold',
                    textDecoration: isPromoCodeValid ? 'line-through' : 'none',
                    fontSize: isPromoCodeValid ? '14px' : '18px',
                  }}
                >
                  {exchangeRates[userLocalCurrency].symbol}
                  {duration == 'monthly'
                    ? formatCurrencyBasedOnCountryCode(
                        parseFloat(
                          subscriptionPriceAccordingToUserLocalCurrency[planName][duration].toFixed(
                            2
                          )
                        ),
                        userLocalCurrency
                      )
                    : formatCurrencyBasedOnCountryCode(
                        parseFloat(
                          (
                            subscriptionPriceAccordingToUserLocalCurrency[planName][duration] / 12
                          ).toFixed()
                        ),
                        userLocalCurrency
                      )}
                </Typography>
                {isPromoCodeValid && (
                  <Typography
                    variant="body2"
                    style={{
                      fontWeight: 'bold',
                      fontSize: '18px',
                      marginLeft: '5px',
                    }}
                  >
                    {exchangeRates[userLocalCurrency].symbol}
                    {duration == 'yearly' ? priceDiscountedMonthly : priceDiscountedYearly}
                  </Typography>
                )}
                <span
                  style={{
                    fontSize: '14px',
                    color: COLOR_GREY_600,
                    textDecoration: 'none !important',
                  }}
                >
                  /month
                </span>
              </Box>
            </Grid>
            <Grid
              item
              container
              direction="row"
              justifyContent="center"
              alignItems="center"
              xs={12}
              sx={{ mt: 2 }}
            >
              <Grid container item className={classes.paymentBorder}>
                {stripeAccount ? (
                  <Payment
                    key={`${product}-${duration}-${userLocalCurrency}-payment`}
                    countryCode={countryCode}
                    city={city}
                    district={district}
                    product={planName}
                    duration={duration}
                    promoCode={promoCode}
                    setSubscriptionModalOpen={setSubscriptionModalOpen}
                    discountedPrice={discountedPrice}
                    countryToUnlock={countryToUnlock}
                    subscriptionPriceAccordingToUserLocalCurrency={
                      subscriptionPriceAccordingToUserLocalCurrency
                    }
                    userLocalCurrency={userLocalCurrency}
                    stripeAccount={stripeAccount}
                  />
                ) : (
                  <LoadingCard height="100%" type="bars" />
                )}
              </Grid>
            </Grid>
            <Grid container item xs={12} sx={{ mt: 2 }}>
              {PromoCodeSection}
            </Grid>
          </>
        )}

        {showTrial && (
          <>
            <Grid container item direction="row" justifyContent="flex-start" xs={12}>
              <Typography sx={{ fontWeight: 'bold', marginTop: '20px' }}>
                Trusted by 10,000 data-driven short-term rental investors.
              </Typography>
            </Grid>

            <Grid
              item
              container
              direction="row"
              justifyContent="center"
              alignItems="center"
              xs={12}
              sx={{
                mt: 2,
                maxHeight: '50vh',
                overflowY: 'auto',
                width: '100%',
              }}
              gap={2}
            >
              {TESTIMONIALS.map(testimonial => (
                <TestimonialCard
                  rating={testimonial.rating}
                  comment={testimonial.comment}
                  reviewer={testimonial.reviewer}
                  reviewerRole={testimonial.reviewerRole}
                  avatar={testimonial.avatar}
                />
              ))}
            </Grid>

            <Box
              sx={{
                position: 'fixed',
                bottom: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 10,
                width: '100%',
                textAlign: 'center',
                borderRadius: collectPayment ? 0 : '24px',
                boxShadow: '0px 6px 16px rgba(0, 0, 0, 0.2)',
                paddingY: 3,
                backgroundColor: 'white',
              }}
            >
              <Button
                variant="contained"
                onClick={() => setCollectPayment(true)}
                sx={{
                  backgroundColor: COLOR_ACCENT_2,
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '18px',
                  borderRadius: '40px',
                  padding: '12px 24px',
                  boxShadow: '0px 6px 16px rgba(0, 0, 0, 0.2)',
                  width: '80%',
                }}
                endIcon={
                  <span role="img" aria-label="rocket">
                    🚀
                  </span>
                }
              >
                Start your 15-Days Free Trial!
              </Button>
            </Box>
          </>
        )}
      </Grid>
    );

    return (
      <Box
        sx={{
          background: 'white',
          width: matches ? '100vw' : '500px',
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          borderRadius: 4,
          outline: 'none !important',
          padding: '15px',
          height: matches ? '100dvh' : '90vh',
          overflow: 'auto',
        }}
      >
        {collectPayment ? OptionalConfirmationPage : DefaultPage}

        {/* Reviews */}
        <SenjaWidget isMobile={true} />
      </Box>
    );
  };

  return (
    <Backdrop open={true} component="div" sx={{ zIndex: 1500 }}>
      <ClickAwayListener
        onClickAway={() => {
          setSubscriptionModalOpen(false);
          window.history.pushState({}, '', window.location.pathname);
          resetSubscriptionModal();
        }}
      >
        {view == 'basic'
          ? BasicSubscriptionModal(basicViewPlanName, collectPayment, setCollectPayment)
          : DetailedSubscriptionModal()}
      </ClickAwayListener>
    </Backdrop>
  );
};

export default SubscriptionModal;
